import { useRef, useEffect, useState } from "react";
import { Box, Heading, TextField } from "@radix-ui/themes";
import { usePayment } from "./payment-context";
import toast from "react-hot-toast";
import { PayCheckoutVariables } from "../../api/pos-api";
import { useTranslation } from "react-i18next";
import { PaymentType } from "../../api/types";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export const appQRSchema = z.object({
  user_id: z.string(),
  user_type: z.string(),
  token: z.string(),
  timestamp: z.string(),
  card_id: z.string(),
  email: z.string(),
  staff_mode: z.boolean().optional(),
});

export function PaymentQrScan() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState("");
  const { makePayment } = usePayment();
  const { t } = useTranslation();

  const parseQR = (value: string): Promise<PayCheckoutVariables["checkin"]> => {
    return new Promise((resolve, reject) => {
      try {
        const parsedQR = JSON.parse(value);
        const validatedQR = appQRSchema.parse(parsedQR);

        resolve(validatedQR);
      } catch (err) {
        toast.error(
          "Cannot parse scanned image. Please check if it's valid QR code",
        );
        setValue("");
        reject(err);
      }
    });
  };

  const onScan = async () => {
    if (value) {
      const checkin = await parseQR(value);
      makePayment({ type: PaymentType.App, checkin });
    }
  };

  useEffect(() => {
    const onKeyPress = function (e: KeyboardEvent) {
      if (["Enter", "Escape"].includes(e.key)) return;

      if ((e.target as HTMLElement)?.tagName !== "INPUT") {
        inputRef.current?.focus();
        setValue(e.key);
        e.preventDefault();
      }
    };

    document.addEventListener("keypress", onKeyPress);

    return () => document.removeEventListener("keypress", onKeyPress);
  }, []);

  return (
    <Box className="w-full h-full flex flex-col items-center justify-between p-8">
      <figure className="flex justify-center mb-4">
        <img src="/phone.png" />
      </figure>
      <Heading
        align="center"
        size="6"
        mb="4"
        mt="2"
        weight="bold"
        color="gray"
        highContrast
      >
        {t("qrScan")}
      </Heading>
      <TextField.Root
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Scan QR code"
        inputMode="none"
        className={twMerge([
          import.meta.env.PROD && "!h-0 overflow-hidden opacity-0",
        ])}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onScan();
          }
        }}
      ></TextField.Root>
    </Box>
  );
}
